import { FunctionComponent, useEffect, useState } from 'react'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Add, Diversity2, NoMeetingRoom, Settings } from '@mui/icons-material'
import styles from './staffHeader.module.scss'
import { useTranslation } from 'react-i18next'
import AddStaffDialog from './Dialog/addStaffDialog'
import { Role } from '@/types/Role'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import { useAuth } from '@/modules/context/AuthContext'
import { Navigate, NavigateFunction, useNavigate } from 'react-router-dom'

interface StaffHeaderProps {
    canDraft: boolean
    refetchAllStaffList?: () => Promise<void>
    getAllStaffInAllDaycares?: (showOnlyWithoutDaycare: boolean) => void
    loadingGetAllStaffInAllDaycares: boolean
}

const StaffHeader: FunctionComponent<StaffHeaderProps> = ({
    canDraft,
    refetchAllStaffList,
    getAllStaffInAllDaycares,
    loadingGetAllStaffInAllDaycares
}: StaffHeaderProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [isAddStaffDialogOpen, setAddStaffDialogOpen] = useState<boolean>(false)

    const autoLinkPrefix: string = (import.meta.env.VITE_PUBLIC_URL || 'https://app.cald.fr') + '/?autoLink='
    const autoLinkSuffix: string = '&role=staff'

    const [autoLinkDaycare, setAutoLinkDaycare] = useState<string>(
        autoLinkPrefix + user?.currentDaycare?.internalAutoLink + autoLinkSuffix
    )
    const [autoLinkActive, setAutoLinkActive] = useState<boolean>(!!user?.currentDaycare?.internalAutoLinkEnable)

    useEffect(() => {
        setAutoLinkDaycare(autoLinkPrefix + user?.currentDaycare?.internalAutoLink + autoLinkSuffix)
        setAutoLinkActive(!!user?.currentDaycare?.internalAutoLinkEnable)
    }, [user?.currentDaycare])

    return user && user.currentDaycare ? (
        <div className={styles.HeaderRow}>
            <div className={styles.MultipleSections}>
                {user.daycares.length > 1 && (
                    <>
                        <DaycareSelector />
                        {user.role === Role.cald && (
                            <div>
                                <ButtonIcon
                                    icon={<Diversity2 />}
                                    disabled={loadingGetAllStaffInAllDaycares}
                                    onClick={() => (getAllStaffInAllDaycares ? getAllStaffInAllDaycares(false) : {})}
                                />
                                <ButtonIcon
                                    icon={<NoMeetingRoom />}
                                    disabled={loadingGetAllStaffInAllDaycares}
                                    onClick={() => (getAllStaffInAllDaycares ? getAllStaffInAllDaycares(true) : {})}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            {canDraft && (
                <div className={styles.Parameters}>
                    <ButtonTextIcon
                        type={'custom'}
                        onClick={() => setAddStaffDialogOpen(true)}
                        customIcon={<Add />}
                        customText={t('inviteStaff')}
                    />
                    {user.currentDaycareRights.canReadParamsDaycare && (
                        <Settings
                            className={styles.Icon}
                            onClick={() => navigate(`/daycare?daycare=${user?.currentDaycare?.id}#staffSettings`)}
                        />
                    )}
                </div>
            )}
            <AddStaffDialog
                open={isAddStaffDialogOpen}
                closeDialog={() => setAddStaffDialogOpen(false)}
                daycareId={user.currentDaycare.id}
                refetchAllStaffList={refetchAllStaffList}
                autoLinkActive={autoLinkActive}
                autoLinkDaycare={autoLinkDaycare}
            />
        </div>
    ) : (
        <Navigate to='/' replace />
    )
}

export default StaffHeader
